import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Heading,
  Highlight,
  Spacer,
  StackProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { Link, LinkBox } from "@areahub/ui/components/link";
import { useRecoilValue } from "recoil";
import { highlightedSearchAtom } from "../filter-bar";
import { PostType } from "../types";
import { Author } from "./author";
import { ImageFragment, PostMedia } from "./media";
import { PostMeta } from "./meta";

export type PostPreviewFragment = {
  __typename?: "GCMS_Post";
  title: string;
  slug: string;
  createdAt: any;
  updatedAt: any;
  published: any;
  excerpt?: string | null;
  readingTime: number;
  postType: PostType;
  author?: {
    __typename?: "Author";
    slug: string;
    person?: {
      __typename?: "Person";
      name: string;
      photo?: {
        __typename?: "Asset";
        id: string;
        url: string;
        width?: number | null;
        height?: number | null;
        handle: string;
      } | null;
    } | null;
  } | null;
  coverImage?: {
    __typename?: "Asset";
    id: string;
    url: string;
    width?: number | null;
    height?: number | null;
    handle: string;
  } | null;
  aggregatePostPage?: {
    __typename?: "AggregatePostPage";
    title?: string | null;
    slug: string;
  } | null;
  trending?: boolean | null | undefined;
};

export interface Asset {
  src?: string;
  width?: number;
  height?: number;
  type?: string;
}
export interface PostPreview extends PostPreviewFragment {
  //todo: find better type for this
  coverImagePlaceholder?: ImageFragment & { alt?: string };
  authorPhotoPlaceholder?: ImageFragment & { alt?: string };
}

export interface PostCardProps extends FlexProps {
  post: PostPreview;
  tagSx?: TextProps;
  authorSx?: StackProps;
  mediaSx?: BoxProps;
}

const PostCard = ({
  post,
  tagSx,
  authorSx,
  mediaSx,
  ...props
}: PostCardProps) => {
  const hasCategory = !!post.aggregatePostPage;
  const categorySlug = post.aggregatePostPage?.slug;
  const postUrl = `/individuals/knowledge-center/${
    hasCategory ? categorySlug + "/" : ""
  }${post.slug}`;
  const categoryUrl = hasCategory
    ? `/individuals/knowledge-center/category/${categorySlug}`
    : undefined;
  const author = post.author;
  const highlight = useRecoilValue(highlightedSearchAtom);
  return (
    <LinkBox as="article" data-cy="post-card">
      <Flex
        direction="column"
        h="100%"
        borderRadius="md"
        overflow="hidden !important"
        boxShadow="md"
        bg="white"
        color="text"
        {...props}
        sx={{
          // Scale the post-media image whenever the trigger(s) are hovered over
          ".post-media-trigger": {
            _hover: {
              ".post-media": {
                transform: "scale(1.05)",
                transition: "transform 0.2s",
              },
            },
          },
        }}
      >
        <Box
          maxW={{ base: "xl", md: "2xl", lg: "7xl" }}
          className="post-media-trigger"
          pb={5}
        >
          {/* The post card image */}
          {post.coverImage && post.title && (
            <PostMedia
              src={post.coverImage.url}
              alt={post.title}
              image={post?.coverImagePlaceholder}
              imageHeight={220}
              imageWidth={608}
              {...mediaSx}
            />
          )}
          <Box px={{ base: "6", md: "8" }} mt="2">
            {/* The post card meta tag */}
            <PostMeta
              type={post.aggregatePostPage?.title || "Other"}
              url={categoryUrl}
            />

            {/* The post card text */}
            <Box>
              {/* Use link overlay to ensure entire card is clickable */}
              <Link
                href={postUrl}
                isOverlay
                apply="styles.textStyles.postCardLink"
              >
                <Heading size="md" mt="4" mb="2" fontWeight="bold">
                  <Highlight
                    query={highlight}
                    styles={{
                      px: "0.5",
                      py: "0.75",
                      borderRadius: "md",
                      bg: "orange.100",
                    }}
                  >
                    {typeof post.title == "string" ? post.title : ""}
                  </Highlight>
                </Heading>
              </Link>
              {/* The post card description: *is* an excerpt and does not need to be truncated any further */}
              <Text color="gray" lineHeight="tall" {...tagSx}>
                {post?.excerpt ? (
                  <Highlight
                    query={highlight}
                    styles={{
                      px: "0.5",
                      py: "0.75",
                      borderRadius: "md",
                      bg: "orange.100",
                    }}
                  >
                    {typeof post.excerpt == "string" ? post.excerpt : ""}
                  </Highlight>
                ) : (
                  ""
                )}
              </Text>
            </Box>
          </Box>
        </Box>
        <Spacer className="post-media-trigger" />
        {/* The post card author and other meta information */}
        {author?.person?.name && author.person.photo && (
          <Author
            name={author.person.name}
            placeholderImage={post?.authorPhotoPlaceholder}
            image={author.person.photo.url}
            meta={`${post.readingTime} min read`}
            url={`/individuals/knowledge-center/author/${author.slug}`}
            py="2"
            my="4"
            px={{ base: "6", md: "8" }}
            tagSx={tagSx}
            {...authorSx}
          />
        )}
      </Flex>
    </LinkBox>
  );
};

export default PostCard;
